<template>
  <div class="accruals-estimates-table" data-testid="accruals-estimates-table">
    <lb-datatableaction
      ref="dataTable"
      auto-height
      :headers="headers"
      :tabledata="rows"
      :displayed-row-count="rows.length"
      :itemsperpage="itemsPerPage"
      :filter-options-data="filterOptionsData"
      :bulk-actions="false"
      :is-data-selected="isPartialSelection"
      :checkalldata="isAllSelected"
      header-bg-class="fbc-table-header-bg"
      data-testid="accruals-estimates-table__datatable"
      @select-all-change="onSelectAllChange"
      @filter-item="$emit('filter-item', $event)"
      @clear-item="$emit('clear-filter')"
      @filter-active-change="$emit('filter-active-change', $event)"
    >
      <template #tbody>
        <template v-for="(item, index) in rows">
          <tr
            :key="item._id"
            class="accruals-estimates-table__row"
            :class="{
              'accruals-estimates-table__row--selected': isRowSelected(item),
              'accruals-estimates-table__row--expanded': isGroupExpanded(item._id),
            }"
            data-testid="accruals-estimates-table__row"
            @click="onRowClick(item, $event)"
          >

            <td
              class="px-4 accruals-estimates-table__vendor-cell"
              :data-testid="`accruals-estimates-table__cell-vendor--${index}`"
            >
              <div class="accruals-estimates-table__vendor-copy">
                <div class="accruals-estimates-table__vendor-title">
                  <button
                    type="button"
                    class="accruals-estimates-table__expand-btn"
                    :aria-expanded="isGroupExpanded(item._id) ? 'true' : 'false'"
                    :aria-label="isGroupExpanded(item._id) ? 'Collapse vendor' : 'Expand vendor'"
                    data-row-nav-ignore
                    :data-testid="`accruals-estimates-table__expand--${index}`"
                    @click.stop="onToggleGroup(item)"
                  >
                    <img
                      :src="assets.ESTIMATES_ROW_CHEVRON"
                      alt=""
                      class="accruals-estimates-table__expand-icon"
                      :class="{ 'accruals-estimates-table__expand-icon--open': isGroupExpanded(item._id) }"
                    />
                  </button>
                  <span class="accruals-estimates-table__vendor-name">{{ item.vendor }}</span>
                </div>
                  <div
                    v-if="item.refTags && item.refTags.length"
                    class="accruals-estimates-table__tags"
                    data-row-nav-ignore
                    :data-testid="`accruals-estimates-table__group-meta--${index}`"
                    @click.stop
                  >
                    <span
                      v-for="tag in visibleRefTags(item)"
                      :key="`${tag.type}-${tag.label}`"
                      class="accruals-estimates-table__tag"
                      :class="`accruals-estimates-table__tag--${tag.type}`"
                    >
                      {{ tag.label }}
                    </span>
                    <span
                      v-if="lineRecordCount(item)"
                      class="accruals-estimates-table__tag accruals-estimates-table__tag--more"
                      :data-testid="`accruals-estimates-table__see-more--${index}`"
                    >
                      {{ lineRecordCount(item) }} records
                    </span>
                  </div>
              </div>
            </td>

            <td
              class="px-4 single-line accruals-estimates-table__location-cell"
              :data-testid="`accruals-estimates-table__cell-location--${index}`"
              :title="item.location || ''"
            >
              {{ item.location || '—' }}
            </td>

            <td
              class="px-4 accruals-estimates-table__amount-cell"
              :class="{
                'accruals-estimates-table__amount-cell--empty': parentFinalAmount(item) == null,
              }"
              :data-testid="`accruals-estimates-table__cell-final--${index}`"
            >
              <span class="accruals-estimates-table__final-amt">
                <span class="accruals-estimates-table__final-amt-value">
                  {{ formatAmount(parentFinalAmount(item)) }}
                </span>
                <span
                  v-if="finalAmountSplit(item)"
                  class="accruals-estimates-table__final-amt-split"
                >
                  {{ finalAmountSplit(item) }}
                </span>
              </span>
            </td>
          </tr>
          <tr
            v-if="isGroupExpanded(item._id)"
            :key="`${item._id}-expand`"
            class="accruals-estimates-table__expand-row"
          >
            <td :colspan="expandColspan">
              <div class="accruals-estimates-table__expand-anim">
                <accruals-estimate-expand-panel
                  :group="item"
                  :source="rowSource(item)"
                  :role="role"
                  :can-review="canReviewRow(item)"
                  :can-approve="canApproveRow(item)"
                  :confirming-id="confirmingGroupId"
                  :saving-id="savingGroupId"
                  :row-menu-items="rowMenuItems"
                  @confirm="onConfirm"
                  @approve="onApproveGroup"
                  @reject="onRejectGroup"
                  @save-gl="onSaveGl"
                  @follow-up="onFollowUp"
                  @open-split-view="onOpenSplitView"
                  @row-menu="onRowMenuSelect"
                />
              </div>
            </td>
          </tr>
        </template>
      </template>
    </lb-datatableaction>
  </div>
</template>

<script>
import { ACCRUALS_ASSETS } from '../accrualsPageConstants';
import AccrualsEstimateExpandPanel from './AccrualsEstimateExpandPanel.vue';
import {
  ACCRUALS_ESTIMATES_TABLE_HEADERS,
  ACCRUALS_ESTIMATE_ROW_MENU_ITEMS,
  ACCRUALS_ESTIMATE_STATUS,
  ACCRUALS_WORKBENCH_ROLES,
  isReviewerWorkbenchRole,
  APPROVER_ACTIONS,
  BULK_COPY,
  canTransition,
  defaultEstimateSource,
  estimateRefLabel,
  formatEstimateMoney,
  isVendorLevelEstimateRow,
  parentFinalAmountSplitLabel,
  validateRejectReason,
  visibleParentFinalAmount,
  visibleVendorRefTags,
  workbenchColumnStatusKey,
} from '../accrualsEstimatesConstants';

export default {
  name: 'AccrualsEstimatesTable',
  components: {
    AccrualsEstimateExpandPanel,
  },
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    selectedIds: {
      type: Array,
      default: () => [],
    },
    itemsPerPage: {
      type: Number,
      default: 25,
    },
    filterOptionsData: {
      type: Array,
      default: () => [],
    },
    selectedRowId: {
      type: String,
      default: null,
    },
    expandedGroupIds: {
      type: Array,
      default: () => [],
    },
    sourceByGroupId: {
      type: Object,
      default: () => ({}),
    },
    confirmingGroupId: {
      type: String,
      default: null,
    },
    savingGroupId: {
      type: String,
      default: null,
    },
    canReview: {
      type: Boolean,
      default: false,
    },
    reviewLocationKeys: {
      type: Array,
      default: () => [],
    },
    approveLocationKeys: {
      type: Array,
      default: () => [],
    },
    role: {
      type: String,
      default: '',
    },
    canApprove: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      assets: ACCRUALS_ASSETS,
      headers: ACCRUALS_ESTIMATES_TABLE_HEADERS,
      rowMenuItems: ACCRUALS_ESTIMATE_ROW_MENU_ITEMS,
    };
  },
  computed: {
    leafRows() {
      return (this.rows || []).filter((row) => row && row.isGroup);
    },
    isPartialSelection() {
      const ids = this.selectedIds || [];
      const leaves = this.leafRows;
      if (!ids.length || !leaves.length || this.isAllSelected) return false;
      const selectedSet = new Set(ids);
      return leaves.some((row) => selectedSet.has(row._id));
    },
    isAllSelected() {
      const ids = this.selectedIds || [];
      const leaves = this.leafRows;
      if (!ids.length || !leaves.length) return false;
      if (ids.length < leaves.length) return false;
      const selectedSet = new Set(ids);
      return leaves.every((row) => row?._id && selectedSet.has(row._id));
    },
    expandColspan() {
      return (this.headers || []).length;
    },
    isApproverRole() {
      return isReviewerWorkbenchRole(this.role)
        || (this.canApprove && !this.canReview);
    },
    reasonDialogTitle() {
      if (this.reasonDialogAction === 'request-resubmission') {
        return 'Request for resubmission';
      }
      if (this.reasonDialogAction === 'exclude') return 'Exclude from cycle';
      return 'Reject estimate';
    },
    reasonDialogConfirmLabel() {
      if (this.reasonDialogAction === 'request-resubmission') return 'Yes, request';
      if (this.reasonDialogAction === 'exclude') return 'Yes, exclude';
      return 'Confirm';
    },
    reasonDialogRefs() {
      return (this.reasonDialogRows || []).map(estimateRefLabel).join(', ');
    },
  },
  beforeDestroy() {
    this.reasonDialogOpen = false;
    this.reasonDialogAction = '';
    this.reasonDialogGroup = null;
    this.reasonDialogRows = [];
    this.reasonText = '';
    this.reasonError = '';
    this.reasonSaving = false;
  },
  methods: {
    locationAllowed(item, keys) {
      if (!item || !(keys || []).length) return false;
      const raw = item.raw || {};
      const candidates = [
        item.locationKey,
        item.subsidiary_code,
        item.location,
        raw.location_key,
        raw.subsidiary_code,
        raw.subsidiary,
        raw.location,
      ]
        .filter((value) => value != null && String(value).trim() !== '')
        .map((value) => String(value).trim().toLowerCase());
      const allowed = new Set(keys.map((key) => String(key).trim().toLowerCase()));
      return candidates.some((value) => allowed.has(value));
    },
    canReviewRow(item) {
      if (!this.canReview) return false;
      return this.locationAllowed(item, this.reviewLocationKeys);
    },
    canApproveRow(item) {
      if (!item || this.canReviewRow(item)) return false;
      return this.locationAllowed(item, this.approveLocationKeys);
    },
    formatAmount(value) {
      return formatEstimateMoney(value);
    },
    parentFinalAmount(item) {
      return visibleParentFinalAmount(item);
    },
    finalAmountSplit(item) {
      return parentFinalAmountSplitLabel(item);
    },
    rowSource(item) {
      const id = item?._id;
      if (id && this.sourceByGroupId && this.sourceByGroupId[id]) {
        return this.sourceByGroupId[id];
      }
      return defaultEstimateSource(item);
    },
    visibleRefTags(item) {
      return visibleVendorRefTags(item?.refTags);
    },
    lineRecordCount(item) {
      return (item?.children || []).filter((row) => row && !isVendorLevelEstimateRow(row)).length;
    },
    isRowSelected(item) {
      return item?._id === this.selectedRowId;
    },
    isGroupExpanded(id) {
      return (this.expandedGroupIds || []).includes(id);
    },
    childIds(item) {
      return item?._id ? [item._id] : [];
    },
    isSelected(id) {
      return (this.selectedIds || []).includes(id);
    },
    toggleSelect(item, checked) {
      const next = new Set(this.selectedIds || []);
      this.childIds(item).forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      const ids = Array.from(next);
      this.$emit('update:selectedIds', ids);
      this.$emit('selection-change', ids);
    },
    onSelectAllChange(checked) {
      if (checked) {
        const ids = this.leafRows.map((r) => r._id).filter(Boolean);
        this.$emit('update:selectedIds', ids);
        this.$emit('selection-change', ids);
      } else {
        this.$emit('update:selectedIds', []);
        this.$emit('selection-change', []);
      }
    },
    clearColumnFilters() {
      this.$refs.dataTable?.clearFilter?.();
    },
    onToggleGroup(item) {
      console.log('[AccrualsEstimatesTable]:', { action: 'toggle-group', groupId: item?._id });
      this.$emit('toggle-group', item);
    },
    onSourceChange(item, source) {
      console.log('[AccrualsEstimatesTable]:', { action: 'source-change', groupId: item?._id, source });
      this.$emit('source-change', { group: item, source });
    },
    onOpenSplitView(item) {
      console.log('[AccrualsEstimatesTable]:', { action: 'split-view', groupId: item?._id });
      this.$emit('open-split-view', item);
    },
    onRowMenuSelect(payload, menuItem) {
      if (payload && payload.row && payload.item) {
        this.$emit('row-menu', payload);
        return;
      }
      this.$emit('row-menu', { group: payload, row: payload, item: menuItem });
    },
    onConfirm(payload) {
      this.$emit('confirm', payload);
    },
    onApproveGroup(payload) {
      this.$emit('approve', payload);
    },
    onRejectGroup(payload) {
      this.$emit('reject', payload);
    },
    onSaveGl(payload) {
      this.$emit('save-gl', payload);
    },
    onFollowUp(group) {
      this.$emit('follow-up', group);
    },
    onRowClick(item, event) {
      const target = event?.target;
      if (target && typeof target.closest === 'function') {
        if (target.closest('button, a, .v-input, .v-checkbox, .v-selection-control, [data-row-nav-ignore]')) {
          return;
        }
      }
      this.onToggleGroup(item);
    },
    groupActionableRows(group, target, role) {
      const actor = role || this.normalizedRole();
      return (group?.children || []).filter((row) => {
        if (!row || isVendorLevelEstimateRow(row)) return false;
        return canTransition(workbenchColumnStatusKey(row), target, actor);
      });
    },
    normalizedRole() {
      const role = String(this.role || '').toLowerCase();
      if (role === ACCRUALS_WORKBENCH_ROLES.APPROVER) return role;
      if (role === ACCRUALS_WORKBENCH_ROLES.MANAGER) return role;
      if (role === ACCRUALS_WORKBENCH_ROLES.PREPARER) return role;
      return this.isApproverRole ? ACCRUALS_WORKBENCH_ROLES.APPROVER : ACCRUALS_WORKBENCH_ROLES.PREPARER;
    },
    groupFooterActions(group) {
      const role = this.normalizedRole();
      const actions = [];
      const submittable = this.groupActionableRows(
        group,
        ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL,
        ACCRUALS_WORKBENCH_ROLES.PREPARER,
      ).filter((row) => {
        const status = workbenchColumnStatusKey(row);
        return this.canReviewRow(row) && (
          status === ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION
          || status === ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL
        );
      });
      const approvable = this.groupActionableRows(
        group,
        ACCRUALS_ESTIMATE_STATUS.APPROVED,
        ACCRUALS_WORKBENCH_ROLES.APPROVER,
      ).filter((row) => this.canApproveRow(row));
      const rejectable = this.groupActionableRows(
        group,
        ACCRUALS_ESTIMATE_STATUS.REJECTED,
        ACCRUALS_WORKBENCH_ROLES.APPROVER,
      ).filter((row) => this.canApproveRow(row));
      const excludable = (group?.children || []).filter((row) => {
        if (!row || isVendorLevelEstimateRow(row)) return false;
        if (!this.canReviewRow(row) && !this.canApproveRow(row)) return false;
        return canTransition(
          workbenchColumnStatusKey(row),
          ACCRUALS_ESTIMATE_STATUS.EXCLUDED,
          role,
        );
      });
      const includable = this.groupActionableRows(
        group,
        ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION,
        ACCRUALS_WORKBENCH_ROLES.PREPARER,
      ).filter((row) => (
        workbenchColumnStatusKey(row) === ACCRUALS_ESTIMATE_STATUS.EXCLUDED
        && this.canReviewRow(row)
      ));
      const resubmission = (group?.children || []).filter((row) => {
        if (!row || isVendorLevelEstimateRow(row)) return false;
        return workbenchColumnStatusKey(row) === ACCRUALS_ESTIMATE_STATUS.APPROVED
          && canTransition(
            ACCRUALS_ESTIMATE_STATUS.APPROVED,
            ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION,
            role,
          );
      });
      if (approvable.length) {
        actions.push({
          id: 'approve-all',
          label: BULK_COPY.APPROVE_ALL,
          primary: true,
          count: approvable.length,
        });
      }
      if (rejectable.length) actions.push({ id: 'reject', label: BULK_COPY.REJECT, count: rejectable.length });
      if (isReviewerWorkbenchRole(role) && resubmission.length) {
        actions.push({
          id: 'request-resubmission',
          label: APPROVER_ACTIONS.REQUEST_RESUBMISSION.label,
          count: resubmission.length,
        });
      }
      if (submittable.length) {
        actions.push({
          id: 'submit',
          label: BULK_COPY.SUBMIT_FOR_APPROVAL,
          primary: !approvable.length,
          disabled: !this.canReview,
          count: submittable.length,
        });
      }
      if (excludable.length) {
        actions.push({ id: 'exclude', label: BULK_COPY.EXCLUDE, count: excludable.length });
      }
      if (includable.length) {
        actions.push({ id: 'include', label: BULK_COPY.INCLUDE, count: includable.length });
      }
      return actions;
    },
    onGroupFooterAction(group, action) {
      console.log('[AccrualsEstimatesTable]:', {
        action: 'group-footer-action',
        actionId: action?.id,
        groupId: group?._id,
      });
      if (!action || !group) return;
      if (action.id === 'submit') {
        this.$emit('submit', {
          group,
          rows: this.groupActionableRows(
            group,
            ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL,
            ACCRUALS_WORKBENCH_ROLES.PREPARER,
          ).filter((row) => (
            workbenchColumnStatusKey(row) === ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION
            && this.canReviewRow(row)
          )),
        });
        return;
      }
      if (action.id === 'approve-all') {
        this.$emit('approve', {
          group,
          rows: this.groupActionableRows(
            group,
            ACCRUALS_ESTIMATE_STATUS.APPROVED,
            ACCRUALS_WORKBENCH_ROLES.APPROVER,
          ).filter((row) => this.canApproveRow(row)),
        });
        return;
      }
      if (action.id === 'exclude') {
        const rows = (group.children || []).filter((row) => {
          if (!row || isVendorLevelEstimateRow(row)) return false;
          if (!this.canReviewRow(row) && !this.canApproveRow(row)) return false;
          return canTransition(
            workbenchColumnStatusKey(row),
            ACCRUALS_ESTIMATE_STATUS.EXCLUDED,
            this.normalizedRole(),
          );
        });
        this.openReasonDialog(action.id, group, rows);
        return;
      }
      if (action.id === 'reject' || action.id === 'request-resubmission') {
        const target = action.id === 'reject'
          ? ACCRUALS_ESTIMATE_STATUS.REJECTED
          : ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION;
        this.openReasonDialog(action.id, group, this.groupActionableRows(group, target));
        return;
      }
      if (action.id === 'include') {
        this.$emit('include', {
          group,
          rows: this.groupActionableRows(group, ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION)
            .filter((row) => workbenchColumnStatusKey(row) === ACCRUALS_ESTIMATE_STATUS.EXCLUDED),
        });
      }
    },
    openReasonDialog(actionId, group, rows) {
      this.reasonDialogAction = actionId;
      this.reasonDialogGroup = group;
      this.reasonDialogRows = rows || [];
      this.reasonText = '';
      this.reasonError = '';
      this.reasonSaving = false;
      this.reasonDialogOpen = true;
      console.log('[AccrualsEstimatesTable]:', {
        action: 'open-reason-dialog',
        actionId,
        groupId: group?._id,
        count: (rows || []).length,
      });
    },
    closeReasonDialog() {
      this.reasonDialogOpen = false;
      this.reasonDialogAction = '';
      this.reasonDialogGroup = null;
      this.reasonDialogRows = [];
      this.reasonText = '';
      this.reasonError = '';
      this.reasonSaving = false;
    },
    onConfirmReasonDialog() {
      if (this.reasonDialogAction === 'exclude' && !String(this.reasonText || '').trim()) {
        this.$emit('exclude', { group: this.reasonDialogGroup, rows: this.reasonDialogRows, reason: '' });
        this.closeReasonDialog();
        return;
      }
      const error = validateRejectReason(this.reasonText);
      this.reasonError = error;
      if (error) {
        this.$store.commit('sbError', error, { root: true });
        console.log('[AccrualsEstimatesTable]:', {
          action: 'reason-validation-failed',
          actionId: this.reasonDialogAction,
          error,
        });
        return;
      }
      const reason = String(this.reasonText).trim();
      const event = this.reasonDialogAction === 'request-resubmission' ? 'request-resubmission' : this.reasonDialogAction;
      console.log('[AccrualsEstimatesTable]:', {
        action: 'reason-confirmed',
        actionId: this.reasonDialogAction,
        groupId: this.reasonDialogGroup?._id,
        refs: (this.reasonDialogRows || []).map(estimateRefLabel),
      });
      this.$emit(event, { group: this.reasonDialogGroup, rows: this.reasonDialogRows, reason });
      this.closeReasonDialog();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AccrualsEstimatesTable.scss';
</style>
